import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('4sat_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('4sat_token');
      localStorage.removeItem('4sat_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (account, password) => api.post('/auth/login', { account, password }),
  refresh: () => api.post('/auth/refresh'),
};

// Devices
export const devicesAPI = {
  getAll: () => api.get('/devices'),
  getDetail: (imei) => api.get(`/devices/${imei}`),
  getLocation: (imei) => api.get(`/devices/${imei}/location`),
  getAllLocations: () => api.get('/devices/locations/all'),
};

// Tracks
export const tracksAPI = {
  getTrack: (imei, beginTime, endTime) => 
    api.get(`/tracks/${imei}`, { params: { begin_time: beginTime, end_time: endTime } }),
};

// Reports
export const reportsAPI = {
  getMileage: (imei, beginTime, endTime) =>
    api.get(`/reports/mileage/${imei}`, { params: { begin_time: beginTime, end_time: endTime } }),
  getTrips: (imei, beginTime, endTime) =>
    api.get(`/reports/trips/${imei}`, { params: { begin_time: beginTime, end_time: endTime } }),
};

// Alarms
export const alarmsAPI = {
  getAlarms: (imei, beginTime, endTime, alarmType = null) =>
    api.get(`/alarms/${imei}`, { 
      params: { 
        begin_time: beginTime, 
        end_time: endTime,
        ...(alarmType && { alarm_type: alarmType })
      } 
    }),
};

// Video
export const videoAPI = {
  getLiveUrl: (imei, channel = 0) => api.get(`/video/live/${imei}`, { params: { channel } }),
  getRtmpUrl: (imei, channel = 0) => api.get(`/video/rtmp/${imei}`, { params: { channel } }),
  getHistory: (imei) => api.get(`/video/history/${imei}`),
  getMedia: (imei, params = {}) => api.get(`/video/media/${imei}`, { params }),
  captureMedia: (imei, camera = 1, mediaType = 2, shootTime = 5) => 
    api.post(`/video/capture/${imei}`, null, { params: { camera, media_type: mediaType, shoot_time: shootTime } }),
  getMultiLive: (imeis) => api.post('/video/multi-live', imeis),
};

// Geofences
export const geofencesAPI = {
  getAll: () => api.get('/geofences'),
  create: (data) => api.post('/geofences', data),
  delete: (imei, fenceId) => api.delete(`/geofences/${imei}/${fenceId}`),
};

// Commands
export const commandsAPI = {
  getAvailable: (imei) => api.get(`/commands/${imei}`),
  send: (imei, instructionName, params = null) => 
    api.post('/commands/send', { imei, instruction_name: instructionName, params }),
  getResult: (cmdId) => api.get(`/commands/result/${cmdId}`),
};

// Groups
export const groupsAPI = {
  getAll: () => api.get('/groups'),
};

// OBD
export const obdAPI = {
  getData: (imei) => api.get(`/obd/${imei}`),
  getFaults: (imei) => api.get(`/obd/fault/${imei}`),
};

// Share
export const shareAPI = {
  getUrl: (imei, expireHours = 24) => api.get(`/share/${imei}`, { params: { expire_hours: expireHours } }),
};

// Brand Settings
export const settingsAPI = {
  getBrand: () => api.get('/settings/brand'),
  getBrandPublic: () => api.get('/settings/brand/public'),  // No auth required
  updateBrand: (settings) => api.put('/settings/brand', settings),
};

// Users Management
export const usersAPI = {
  getAll: () => api.get('/users'),
  getOne: (userId) => api.get(`/users/${userId}`),
  create: (userData) => api.post('/users', userData),
  update: (userId, userData) => api.put(`/users/${userId}`, userData),
  delete: (userId) => api.delete(`/users/${userId}`),
};

// Cache
export const cacheAPI = {
  clear: () => api.post('/cache/clear'),
};

export default api;
